/* eslint-disable jsx-a11y/anchor-is-valid */
import { FC, useState } from "react";
import { Label, TextInput } from "flowbite-react"; // assuming Card is not used
import { useTranslation } from "react-i18next";
import { HiCheckCircle } from "react-icons/hi";
import { ValidationError } from "../../types/ValidationError";
import secondaryLogo from "../../assets/logo/logibud_secondary_white.svg";
import { LanguageDropdown, Button } from "@components";
import { GoogleAuthButton } from "../molecules/GoogleAuthButton";

interface SignupFormProps {
  onClicksignup: (email: string, password: string) => any;
  onClickGoogleSignup?: () => any;
  validation: ValidationError;
  isProcessing?: boolean;
}

export const SignupForm: FC<SignupFormProps> = ({
  onClicksignup,
  onClickGoogleSignup,
  validation,
  isProcessing = false,
}) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const { t } = useTranslation(["common", "user"]);

  const passwordsMatch =
    password.length > 0 &&
    confirmPassword.length > 0 &&
    password === confirmPassword;
  const passwordsDoNotMatch =
    password.length > 0 &&
    confirmPassword.length > 0 &&
    password !== confirmPassword;

  const handleSignup = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (passwordsDoNotMatch) {
      return;
    }

    onClicksignup(email, password);
  };

  const uspList =
    t("user:login.signup_banner_usp_list", { returnObjects: true }) || [];

  return (
    <div className="relative flex flex-col items-center justify-center h-screen lg:gap-y-12 bg-white p-0">
      <div className="absolute top-0 right-4 p-4 z-10">
        <LanguageDropdown />
      </div>
      <div className="grid lg:h-screen lg:grid-cols-2 w-full h-full md:w-full md:h-full">
        <div className="flex items-center justify-center px-4 py-6 lg:py-0 h-full md:w-full md:h-full">
          <form
            onSubmit={handleSignup}
            className="w-full max-w-xl"
            autoComplete="off"
          >
            <p className="text-2xl font-medium mb-5">
              {t("user:login.signup")}
            </p>
            <div className="mb-4 flex flex-col gap-y-3 ">
              <Label htmlFor="email" style={{ color: "#878789" }}>
                {t("user:login.email.label")}
              </Label>
              <TextInput
                id="email"
                name="email"
                placeholder={t("user:login.email.placeholder")}
                type="email"
                value={email}
                style={{ fontSize: 20 }}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="username"
                color={
                  validation.errorFields &&
                  validation.errorFields.includes("email")
                    ? "failure"
                    : "grey"
                }
              />
            </div>
            <div className="mb-4 flex flex-col gap-y-3 ">
              <Label style={{ color: "#878789" }} htmlFor="password">
                {t("user:login.password.label")}
              </Label>
              <TextInput
                id="password"
                name="password"
                placeholder={t("user:login.password.placeholder")}
                type="password"
                value={password}
                style={{ fontSize: 20 }}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                color={
                  validation.errorFields &&
                  validation.errorFields.includes("password")
                    ? "failure"
                    : passwordsMatch
                      ? "success"
                      : "grey"
                }
              />
            </div>
            <div className="mb-4 flex flex-col gap-y-3 ">
              <Label style={{ color: "#878789" }} htmlFor="confirmPassword">
                {t("user:login.confirm_password.label")}
              </Label>
              <TextInput
                id="confirmPassword"
                name="confirmPassword"
                placeholder={t("user:login.confirm_password.placeholder")}
                type="password"
                value={confirmPassword}
                style={{ fontSize: 20 }}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
                color={
                  passwordsDoNotMatch
                    ? "failure"
                    : passwordsMatch
                      ? "success"
                      : "grey"
                }
              />
            </div>
            {passwordsMatch && (
              <div className="mb-4 flex flex-col gap-y-3">
                <p className="mt-1 text-sm text-green-600 dark:text-green-500">
                  {t("user:login.passwords_match")}
                </p>
              </div>
            )}
            {passwordsDoNotMatch && (
              <div className="mb-4 flex flex-col gap-y-3">
                <p className="mt-1 text-sm text-red-600 dark:text-red-500">
                  {t("user:login.passwords_do_not_match")}
                </p>
              </div>
            )}
            {validation.isError && (
              <div className="mb-4 flex flex-col gap-y-3">
                <p className="mt-1 text-sm text-red-600 dark:text-red-500">
                  {validation.errorMessage}
                </p>
              </div>
            )}
            <div className="mb-4">
              <Button
                type="submit"
                className="w-full bg-lgb-primary rounded-full h-14 transition duration-300 ease-in-out transform  hover:bg-lgb-primary-dark flex justify-center items-center"
                isProcessing={isProcessing}
                style={{
                  boxShadow: "-5px 4px 24px -4px rgba(114,20,255,0.42)", // Adjusted shadow values
                }}
              >
                {t("user:login.signup")}
              </Button>
            </div>
            {onClickGoogleSignup && (
              <>
                <div className="mb-4 flex items-center gap-4 text-lgb-grey-400">
                  <div className="h-px flex-1 bg-lgb-grey-100 dark:bg-[#3c4568]" />
                  <span className="text-sm">{t("user:login.or")}</span>
                  <div className="h-px flex-1 bg-lgb-grey-100 dark:bg-[#3c4568]" />
                </div>
                <div className="mb-4">
                  <GoogleAuthButton
                    label={t("user:login.continue_with_google")}
                    disabled={isProcessing}
                    onClick={onClickGoogleSignup}
                  />
                </div>
              </>
            )}
            <p className="text-md mt-12 text-lgb-grey-600">
              {t("user:login.already_registered")}&nbsp;
              <a href="/login" className="text-lgb-primary hover:underline">
                {t("user:login.go_to_login")}
              </a>
            </p>
          </form>
        </div>
        <div
          className="flex items-center justify-center px-4 py-6 lg:py-0 sm:px-6 "
          style={{
            backgroundImage:
              "linear-gradient(135deg, rgba(6,11,39,1) 0%, #0c164d 100%)",
          }}
        >
          <div className="max-w-md xl:max-w-xl w-full h-full md:w-full md:h-full justify-center flex-col content-center">
            <a
              href="#"
              className="flex items-center mb-4 text-2xl font-semibold text-darkBlue"
            >
              <img className="h-8 mr-2" src={secondaryLogo} alt="logo" />
            </a>
            <h1 className="mb-4 text-3xl font-extrabold leading-none tracking-tight text-white xl:text-5xl">
              {t("user:login.signup_banner_title")}
            </h1>
            <p className="mb-4 font-light text-white lg:mb-8">
              {t("user:login.signup_banner_description")}
            </p>
            <p className="mb-4 font-light text-white lg:mb-8">
              {t("user:login.signup_banner_usp_intro")}
            </p>
            <ul className="space-y-2 text-white">
              {Array.isArray(uspList) &&
                uspList.map((usp, index) => (
                  <li key={index} className="flex items-center">
                    <HiCheckCircle className="h-5 w-5 mr-2 text-green-400" />
                    {usp}
                  </li>
                ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignupForm;
